/* ==========================================
   PORTFOLIO SHOWCASE & MODAL CASE STUDY VIEWER
   Category filtering, project search, & overlay detailed modal
   ========================================== */

const veloxImage = new URL('../assets/velox-commerce.png', import.meta.url).href;

const portfolioProjects = [
  {
    id: 'ai-health',
    title: 'MedVision AI - Diagnostic Imaging Assistant',
    category: 'ai-eng',
    tag: 'Generative AI & Vision',
    img: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=800&auto=format&fit=crop',
    desc: 'Deep learning computer vision system detecting early-stage pulmonary abnormalities from X-Rays with 99.4% precision.',
    stack: ['Python', 'PyTorch', 'FastAPI', 'React', 'Docker'],
    challenge: 'High latency during real-time DICOM image processing in hospital networks.',
    solution: 'Engineered custom CUDA kernel acceleration and model quantization, reducing inferencing latency from 4.2s to 180ms.',
    results: 'Deployed in 24 hospital networks processing over 50,000 scans daily with 40% diagnostic speed improvement.'
  },
  {
    id: 'fintech-saas',
    title: 'Apex Trading - AI Algorithmic Platform',
    category: 'full-stack',
    tag: 'Enterprise SaaS',
    img: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?q=80&w=800&auto=format&fit=crop',
    desc: 'Real-time financial analytics platform with predictive risk modeling and automated sub-millisecond trade routing.',
    stack: ['Next.js', 'Node.js', 'Go', 'PostgreSQL', 'Redis', 'AWS'],
    challenge: 'Handling 100,000+ simultaneous WebSocket data streams during market volatility.',
    solution: 'Designed a decoupled distributed microservice architecture using Redis Pub/Sub and Go event loops.',
    results: 'Scaled to $1.2B daily volume with zero downtime during record volatility events.'
  },
  {
    id: 'llm-agent',
    title: 'OmniSupport - Autonomous Customer AI Agent',
    category: 'llm-agents',
    tag: 'LLM & Agents',
    img: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?q=80&w=800&auto=format&fit=crop',
    desc: 'Multi-agent LLM customer support ecosystem autonomously resolving complex multi-step technical inquiries.',
    stack: ['LangChain', 'OpenAI API', 'VectorDB', 'TypeScript', 'Tailwind'],
    challenge: 'Preventing LLM hallucinations while querying confidential enterprise ERP documentation.',
    solution: 'Implemented hybrid dense-sparse RAG retrieving contextual vectors with strict guardrail validation.',
    results: 'Automated 78% of tier-1 and tier-2 support tickets, saving $1.4M annually.'
  },
  {
    id: 'supply-chain',
    title: 'LogiFlow - Smart Logistics & Route Optimizer',
    category: 'full-stack',
    tag: 'Cloud & IoT',
    img: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=800&auto=format&fit=crop',
    desc: 'AI-powered fleet routing platform reducing fuel consumption and predicting supply chain bottlenecks.',
    stack: ['React Native', 'Node.js', 'Python', 'Kubernetes', 'MongoDB'],
    challenge: 'Dynamic route calculation across 12,000 active trucks in adverse weather conditions.',
    solution: 'Developed spatial graph neural network predicting traffic delays 3 hours in advance.',
    results: 'Cut total fleet fuel costs by 22% and increased on-time deliveries to 98.7%.'
  },
  {
    id: 'retail-recommendation',
    title: 'Velox Commerce - Personalized AI Shopping',
    category: 'ai-eng',
    tag: 'Recommendation Systems',
    img: veloxImage,
    desc: 'Hyper-personalized recommendation engine for an e-commerce giant with 5M+ monthly active users.',
    stack: ['Python', 'TensorFlow', 'GraphQL', 'AWS SageMaker', 'Next.js'],
    challenge: 'Cold-start recommendation accuracy for brand new store inventory.',
    solution: 'Integrated multimodal embeddings combining text attributes and visual feature vectors.',
    results: 'Increased average order value (AOV) by 34% and boosted recommendation CTR by 2.8x.'
  },
  {
    id: 'mobile-fitness',
    title: 'AuraFit - Motion Tracking Fitness App',
    category: 'mobile',
    tag: 'Mobile & Computer Vision',
    img: 'https://images.unsplash.com/photo-1517838277536-f5f99be501cd?q=80&w=800&auto=format&fit=crop',
    desc: 'Mobile application utilizing device camera for real-time posture analysis and rep counting.',
    stack: ['Flutter', 'MediaPipe', 'Firebase', 'Python', 'FastAPI'],
    challenge: 'Running heavy 30fps pose estimation on budget mobile devices without battery drain.',
    solution: 'Optimized MediaPipe ML pipeline with light spatial downsampling.',
    results: 'Achieved 4.9-star App Store rating with over 250,000 active global users.'
  }
];

document.addEventListener('DOMContentLoaded', () => {
  const grid = document.getElementById('portfolio-grid');
  const tabsContainer = document.getElementById('portfolio-tabs');
  const modalOverlay = document.getElementById('case-study-modal');
  const modalBody = document.getElementById('modal-body-content');
  const modalClose = document.getElementById('modal-close-btn');

  if (!grid) return;

  let lastFocusedElement = null;

  function renderProjects(filter = 'all') {
    grid.innerHTML = '';
    const filtered = filter === 'all' 
      ? portfolioProjects 
      : portfolioProjects.filter(p => p.category === filter);

    if (filtered.length === 0) {
      grid.innerHTML = `
        <div class="glass-card" style="grid-column: 1 / -1; text-align: center; padding: 4rem 2rem;">
          <h3 style="margin-bottom: 0.75rem;">No case studies found</h3>
          <p style="color: var(--text-muted);">We are actively indexing new enterprise case studies for this category.</p>
        </div>
      `;
      return;
    }

    filtered.forEach(p => {
      const card = document.createElement('div');
      card.className = 'glass-card portfolio-card reveal-on-scroll is-visible';
      card.innerHTML = `
        <div class="portfolio-img-container">
          <img src="${p.img}" alt="${p.title}" loading="lazy" width="800" height="500">
        </div>
        <div class="portfolio-content">
          <span class="pill-badge" style="margin-bottom: 0.75rem; align-self: flex-start;">${p.tag}</span>
          <h3 style="margin-bottom: 0.75rem; font-size: 1.35rem;">${p.title}</h3>
          <p style="margin-bottom: 1.25rem; font-size: 0.95rem;">${p.desc}</p>
          <div style="display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: auto;">
            ${p.stack.map(s => `<span style="font-size: 0.75rem; background: rgba(255,255,255,0.06); padding: 0.2rem 0.6rem; border-radius: 4px; color: var(--text-muted);">${s}</span>`).join('')}
          </div>
          <div class="portfolio-actions">
            <button class="btn btn-primary view-case-btn" data-id="${p.id}" aria-label="View case study for ${p.title}" style="padding: 0.6rem 1.2rem; font-size: 0.85rem; flex: 1;">
              Case Study
            </button>
          </div>
        </div>
      `;
      grid.appendChild(card);
    });

    // Attach click listeners to case study buttons
    document.querySelectorAll('.view-case-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        lastFocusedElement = e.currentTarget;
        const id = e.currentTarget.getAttribute('data-id');
        openModal(id);
      });
    });
  }

  // Handle Tab Filtering
  if (tabsContainer) {
    tabsContainer.addEventListener('click', (e) => {
      if (e.target.classList.contains('tab-btn')) {
        document.querySelectorAll('#portfolio-tabs .tab-btn').forEach(b => {
          b.classList.remove('active');
          b.setAttribute('aria-selected', 'false');
        });
        e.target.classList.add('active');
        e.target.setAttribute('aria-selected', 'true');
        const filter = e.target.getAttribute('data-filter');
        renderProjects(filter);
      }
    });
  }

  // Close Modal Helper
  function closeModal() {
    if (!modalOverlay) return;
    modalOverlay.classList.remove('active');
    if (lastFocusedElement) {
      lastFocusedElement.focus();
      lastFocusedElement = null;
    }
  }

  // Modal Open Function
  function openModal(id) {
    const project = portfolioProjects.find(p => p.id === id);
    if (!project || !modalOverlay || !modalBody) return;

    modalBody.innerHTML = `
      <div style="margin-bottom: 1.5rem;">
        <span class="pill-badge purple" style="margin-bottom: 0.5rem;">${project.tag}</span>
        <h2 style="font-size: 2rem; margin-bottom: 0.75rem;">${project.title}</h2>
      </div>
      <img src="${project.img}" alt="${project.title}" style="width: 100%; height: 300px; object-fit: cover; border-radius: var(--radius-md); margin-bottom: 2rem;">
      
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; margin-bottom: 2rem;">
        <div class="glass-card" style="padding: 1.25rem;">
          <h4 style="color: var(--primary-accent); margin-bottom: 0.5rem;">The Challenge</h4>
          <p style="font-size: 0.95rem;">${project.challenge}</p>
        </div>
        <div class="glass-card" style="padding: 1.25rem;">
          <h4 style="color: var(--secondary-accent); margin-bottom: 0.5rem;">Our Solution</h4>
          <p style="font-size: 0.95rem;">${project.solution}</p>
        </div>
      </div>

      <div class="glass-panel" style="padding: 1.5rem; margin-bottom: 2rem; background: rgba(56, 189, 248, 0.05);">
        <h4 style="color: var(--cyan-accent); margin-bottom: 0.5rem;">Key Results & ROI</h4>
        <p style="font-size: 1.05rem; font-weight: 600; color: var(--text-primary);">${project.results}</p>
      </div>

      <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem;">
        <div>
          <span style="font-size: 0.85rem; color: var(--text-muted); display: block; margin-bottom: 0.4rem;">Tech Stack:</span>
          <div style="display: flex; gap: 0.5rem; flex-wrap: wrap;">
            ${project.stack.map(s => `<span class="pill-badge">${s}</span>`).join('')}
          </div>
        </div>
        <button class="btn btn-primary" id="modal-action-btn" onclick="alert('Demo environment initialization requested.')">Request Demo Environment</button>
      </div>
    `;

    modalOverlay.classList.add('active');

    // Shift focus to close button
    if (modalClose) {
      setTimeout(() => modalClose.focus(), 50);
    }
  }

  if (modalClose && modalOverlay) {
    modalClose.addEventListener('click', closeModal);
    modalOverlay.addEventListener('click', (e) => {
      if (e.target === modalOverlay) closeModal();
    });
  }

  // Keyboard accessibility: Escape key closes modal
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modalOverlay && modalOverlay.classList.contains('active')) {
      closeModal();
    }
  });

  // Initial render
  renderProjects('all');
});
